// Apolo IDE — SidePanel root + tab nav (Preview/Diff/Terminal/Files/Tasks/Plan).
//
// AnimatePresence-style behaviour is provided by simple CSS transition for
// Epic 5 — Framer Motion polish can be added later without API changes.

import { useEffect } from 'react';
import { Bot, BookOpen, Brain, BrainCircuit, Bug, CheckSquare, Eye, FileDiff, FileEdit, FileText, FlaskConical, GitBranch, GraduationCap, Layers, Lightbulb, ListTodo, Map, Route, ScrollText, Search, ShieldCheck, Sparkles, Terminal, X } from 'lucide-react';
import PreviewTab from './side-panel/PreviewTab.jsx';
import DiffTab from './side-panel/DiffTab.jsx';
import TerminalTab from './side-panel/TerminalTab.jsx';
import FilesTab from './side-panel/FilesTab.jsx';
import TasksTab from './side-panel/TasksTab.jsx';
import PlanTab from './side-panel/PlanTab.jsx';
import RulesTab from './side-panel/RulesTab.jsx';
import SessionRulesTab from './side-panel/SessionRulesTab.jsx';
import LearnedRules from './side-panel/LearnedRules.jsx';
import EditsTab from './side-panel/EditsTab.jsx';
import TestsTab from './side-panel/TestsTab.jsx';
import DualModeTab from './side-panel/DualModeTab.jsx';
import BrainTab from './side-panel/BrainTab.jsx';
import LearningPanel from './side-panel/LearningPanel/LearningPanel.jsx';
import MultiAgentPanel from './MultiAgent/MultiAgentPanel.jsx';
import TaskPanel from './MultiAgent/TaskPanel.jsx';
import GuardrailsPanel from './guardrails/GuardrailsPanel.jsx';
import AdapterView from './side-panel/AdapterView.jsx';
import WorktreePanel from './side-panel/WorktreePanel.jsx';
import TodosPanel from './side-panel/TodosPanel.jsx';
import MemoryEditor from './side-panel/MemoryEditor.jsx';
import GrepResults from './side-panel/GrepResults.jsx';
import SidePanelDrawer from './side-panel/SidePanelDrawer.jsx';
import BadRunsPanel from './side-panel/BadRunsPanel.jsx';

// (Pedro) — "Mapa do Código" was duplicated: a per-session SidePanel tab here
// AND the canonical top-level "Mapa do Código" view in the left sidebar. The
// SidePanel copy (CodeMapPanel + its codemap tab) was removed; the left-sidebar
// view stays. The CodeMap component itself is unchanged (still used top-level).

// eslint-disable-next-line react-refresh/only-export-components
export const SIDE_PANEL_TABS = [
  { id: 'preview', label: 'Preview', icon: Eye, Component: PreviewTab },
  { id: 'diff', label: 'Diff', icon: FileDiff, Component: DiffTab },
  { id: 'terminal', label: 'Terminal', icon: Terminal, Component: TerminalTab },
  { id: 'files', label: 'Files', icon: FileText, Component: FilesTab, needsSession: true },
  { id: 'tasks', label: 'Tasks', icon: ListTodo, Component: TasksTab },
  { id: 'plan', label: 'Plan', icon: Map, Component: PlanTab },
  // Sprint v11 E4 — .apolorules editor per session
  { id: 'rules', label: 'Rules', icon: ScrollText, Component: RulesTab, needsSession: true },
  // Sprint self-improving E2 — ephemeral rules learned this session (V2)
  { id: 'session-rules', label: 'Session', icon: Lightbulb, Component: SessionRulesTab, needsSession: true },
  // Sprint self-improving E3 — batch evaluator + codified .apolorules rules (V3)
  { id: 'learned-rules', label: 'Evaluator', icon: GraduationCap, Component: LearnedRules, needsSession: true },
  // Sprint v11 E6 — Multi-file edits (XML batch with atomic apply)
  { id: 'edits', label: 'Edits', icon: FileEdit, Component: EditsTab },
  // Sprint v11 E9 — Auto-test runner (vitest/jest output parser + retry loop)
  { id: 'tests', label: 'Tests', icon: FlaskConical, Component: TestsTab },
  // Sprint v11 E8 — Architect/Implementer dual-model (self-contained side panel)
  { id: 'dual', label: 'Dual', icon: BrainCircuit, Component: DualModeTab, needsSession: true },
  // Sprint v6 B2 — IDE Tier S panels
  { id: 'worktree', label: 'Worktree', icon: GitBranch, Component: WorktreePanel, needsSession: true },
  { id: 'todos', label: 'Todos', icon: CheckSquare, Component: TodosPanel, needsSession: true },
  { id: 'memory', label: 'Memory', icon: BookOpen, Component: MemoryEditor },
  { id: 'grep', label: 'Grep', icon: Search, Component: GrepResults, needsSession: true },
  // (codemap removed — see note above; canonical Mapa do Código is top-level)
  { id: 'brain', label: 'Brain', icon: Brain, Component: BrainTab },
  { id: 'learning', label: 'Learning', icon: Sparkles, Component: LearningPanel },
  // S2: DAG Task Panel — decompose → DAG board → approve/reject.
  { id: 'dag', label: 'DAG', icon: Route, Component: TaskPanel, needsSession: true },
  // S4: Auto-review + Checkpoints (guard-rails surface).
  { id: 'guardrails', label: 'Guard', icon: ShieldCheck, Component: GuardrailsPanel, needsSession: true },
  // Self-Improve Hardening Stage 1 (E4) — failed runs (ABORT / red verdicts) with
  // deterministic replay shown live. Cross-session (reads persisted turn_trace).
  { id: 'bad-runs', label: 'Bad runs', icon: Bug, Component: BadRunsPanel },
  // S5: MultiAgent Panel (⇧⌘A). Tab id is 'agents' to match the shortcut
  // handler in SessionWorkspace.
  { id: 'agents', label: 'Agents', icon: Bot, Component: MultiAgentPanel },
  // Sprint v5 A1: Adapters catalog (⇧⌘E — "Endpoints"; D/P already bound).
  { id: 'adapters', label: 'Adapters', icon: Layers, Component: AdapterView },
];

// (Pedro 2026-06-25, sprint cursor-strip) — RAIL: superfícies primárias do
// SidePanel ordenadas pra rail vertical "Cursor-style". Rail mostra ~10
// ícones; o resto continua acessível via Command Center (⌘⇧O). Quem precisa
// de uma superfície que NÃO está aqui muda activeTab pelo CC e o body
// renderiza normal — o rail só destaca se acertou um dos primários.
const RAIL_TAB_IDS = [
  'preview',
  'diff',
  'terminal',
  'files',
  'tasks',
  'plan',
  'edits',
  'tests',
  'brain',
  'agents',
];

export default function SidePanel({ open, activeTab, onTabChange, onClose, sessionId, sessionDirectory }) {
  // Close panel on Escape — common UX pattern, no need to mouse over the X.
  // (Hook deve vir ANTES de qualquer early return — rules-of-hooks)
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!open) return null;
  const tab = SIDE_PANEL_TABS.find((t) => t.id === activeTab) ?? SIDE_PANEL_TABS[0];
  const Body = tab.Component;
  // MultiAgentPanel takes sessionId; others ignore extra props.
  // 'agents' historically takes sessionId; v6 B2 panels (worktree, todos,
  // grep) advertise needsSession=true on the tab config and receive it too.
  // 'learning' is not session-gated, but its craft-rule pump needs the active
  // worktree to write .apolorules + materialize craft tests, so it gets the session too.
  const bodyProps = tab.id === 'agents' || tab.id === 'learning' || tab.needsSession ? { sessionId, sessionDirectory } : {};

  // Rail items derivados na ordem fixa do RAIL_TAB_IDS (não da SIDE_PANEL_TABS).
  const railItems = RAIL_TAB_IDS
    .map((id) => SIDE_PANEL_TABS.find((t) => t.id === id))
    .filter(Boolean);

  // Sprint v9 M3: usa SidePanelDrawer pra renderizar responsivamente —
  // inline (xl+), slide-in drawer (md..xl), fullscreen sheet (<md).
  // Antes o aside era `hidden lg:flex`, deixando mobile sem acesso.
  return (
    <SidePanelDrawer open={open} onClose={onClose}>
      <header
        className="flex items-center justify-between border-b border-apolo-borderSubtle px-3 py-2"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2 text-sm font-medium text-apolo-textPrimary">
          {tab?.icon && <tab.icon size={14} aria-hidden="true" className="text-apolo-textSecondary" />}
          <span>{tab?.label || ''}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar painel"
          title="Fechar painel (Esc)"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 lg:h-7 lg:w-7"
        >
          <X size={16} className="lg:h-[14px] lg:w-[14px]" aria-hidden="true" />
        </button>
      </header>
      <div className="flex min-h-0 flex-1">
        {/* (Pedro 2026-06-25, sprint cursor-strip) Rail vertical estreito
           à esquerda do body — 10 superfícies primárias. Click muda
           activeTab. Tab fora do rail (raras) segue acessível pelo
           Command Center. */}
        {onTabChange && (
          <nav
            aria-label="Superfícies do painel"
            data-testid="side-panel-rail"
            className="flex w-8 shrink-0 flex-col gap-0.5 border-r border-apolo-borderSubtle bg-apolo-surface1/40 py-1"
          >
            {railItems.map((rt) => {
              const isActive = rt.id === tab.id;
              const Icon = rt.icon;
              return (
                <button
                  key={rt.id}
                  type="button"
                  onClick={() => onTabChange(rt.id)}
                  aria-pressed={isActive}
                  aria-label={rt.label}
                  title={rt.label}
                  data-testid={`side-panel-rail-${rt.id}`}
                  className={
                    'mx-auto inline-flex h-7 w-7 items-center justify-center rounded-md transition focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accent/40 ' +
                    (isActive
                      ? 'bg-apolo-surface3 text-apolo-textPrimary'
                      : 'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textSecondary')
                  }
                >
                  {Icon ? <Icon size={13} aria-hidden="true" /> : null}
                </button>
              );
            })}
          </nav>
        )}
        <div className="flex-1 overflow-y-auto">
          <Body {...bodyProps} />
        </div>
      </div>
    </SidePanelDrawer>
  );
}
